/**
 * AI arbejder lige nu ([D-439], 12-conventions §95) — én fælles poll af
 * /api/ai-activity for hele appen. Sidebaren og sidens header læser herfra,
 * så de altid er enige, og der kun går ét kald pr. fane.
 *
 * Serveren registrerer selv hvert sprogmodel- og billedkald (lib/ai-activity.ts)
 * og viser kun punkter på sider brugeren har adgang til. Pollen kører hvert
 * 10. sekund, mens fanen er synlig. En knap der starter en kørsel, kalder
 * `noteAiStarted()`, så prikkerne kommer frem med det samme frem for ved
 * næste poll.
 */

import { reactive, watch } from 'vue';

import { auth } from './useAuth';

export interface AiActivityItem {
  taskKey: string;
  label: string;
  pagePaths: string[];
  startedAt: number;
  automatic: boolean;
}

const POLL_MS = 10_000;

export const aiActivity = reactive<{ items: AiActivityItem[] }>({ items: [] });

let started = false;
let timer: number | undefined;
let inflight = false;

async function poll(): Promise<void> {
  if (!auth.user) {
    aiActivity.items = [];
    return;
  }
  if (inflight || document.visibilityState === 'hidden') return;
  inflight = true;
  try {
    const res = await fetch('/api/ai-activity', { credentials: 'same-origin' });
    if (!res.ok) {
      aiActivity.items = [];
      return;
    }
    const data = (await res.json()) as { items?: AiActivityItem[] };
    aiActivity.items = Array.isArray(data.items) ? data.items : [];
  } catch {
    /* prikkerne er ikke kritiske; næste poll prøver igen */
  } finally {
    inflight = false;
  }
}

/** Starter den fælles poll (idempotent). Kaldes af sidebaren og headeren. */
export function startAiActivityPolling(): void {
  if (started) return;
  started = true;
  void poll();
  timer = window.setInterval(() => void poll(), POLL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void poll();
  });
  // Auth bootstrapper asynkront: poll igen, når brugeren er kendt.
  watch(
    () => auth.user,
    () => void poll(),
  );
}

/** Kaldes lige efter en knap har startet en AI-kørsel: prikkerne frem nu. */
export function noteAiStarted(): void {
  window.setTimeout(() => void poll(), 1_200);
  window.setTimeout(() => void poll(), 4_000);
}

/** Til tests og hot-reload. */
export function stopAiActivityPolling(): void {
  if (timer != null) window.clearInterval(timer);
  timer = undefined;
  started = false;
}

/**
 * Punkter der hører til et MENUPUNKT: siden selv og alt under den, så både
 * hovedmenuen (/webshop), undermenuen (/webshop/images) og siden
 * (/webshop/images/review) får prikkerne.
 *
 * `exact` til menupunkter der ER en side (niveau 3 og undermenuer uden
 * børn): en sektions-rod som Organic SoMe → Oversigt (/marketing/organic-social)
 * må ikke arve prikkerne fra søstersiden Overvågning, der ligger under samme sti.
 */
export function aiActivityForMenu(path: string, exact = false): AiActivityItem[] {
  if (!path) return [];
  return aiActivity.items.filter((it) =>
    it.pagePaths.some((p) => p === path || (!exact && p.startsWith(path + '/'))),
  );
}

/**
 * Punkter der hører til den SIDE man står på: siden selv, en underside af
 * den (fx en rapport under /strategy/diagnosis/:id) eller en side under den.
 */
export function aiActivityForPage(routePath: string): AiActivityItem[] {
  return aiActivity.items.filter((it) =>
    it.pagePaths.some((p) => p === routePath || routePath.startsWith(p + '/') || p.startsWith(routePath + '/')),
  );
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
}

/** Tooltip-tekst: hvad AI arbejder på, og siden hvornår. */
export function aiActivityTip(items: AiActivityItem[]): string {
  if (items.length === 0) return '';
  const lines = items.map(
    (it) => `${it.label}, ${it.automatic ? 'automatisk kørsel ' : ''}startet kl. ${fmtTime(it.startedAt)}`,
  );
  return `AI arbejder: ${lines.join(' · ')}`;
}
